import { type FC, useCallback, useEffect, useMemo } from 'react'

import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'

import { Error } from '@/components/ui/Error.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormCheckbox } from '@/components/ui/form/form-checkbox.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import {
  type FormValues,
  initialValues,
  resolver,
} from '@/dialogs/TiersDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import {
  $tierError,
  $tiers,
  createTierFx,
  updateTierFx,
} from '@/models/lists/tiers.ts'

interface Props extends DialogProps {
  tierId?: number
  testId?: string
}

export const TiersDialog: FC<Props> = ({ onClose, isOpen, tierId, testId }) => {
  const isUpdating = useUnit(updateTierFx.pending)
  const isCreating = useUnit(createTierFx.pending)
  const error = useUnit($tierError)
  const tiers = useUnit($tiers)
  const tier = useMemo(
    () => tiers.find(({ id }) => id === tierId),
    [tierId, tiers],
  )
  const initial = useMemo(() => {
    if (tier) {
      const { id, onlyFansId, ...sorted } = tier

      return sorted
    }

    return initialValues
  }, [tier])

  const addForm = useForm<FormValues>({
    defaultValues: initial,
    mode: 'all',
    resolver,
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    // Trigger validation when one of those changed
    const subscription = addForm.watch(() => {
      addForm.trigger('maxSpend')
      addForm.trigger('minSpend')
    })

    return () => subscription.unsubscribe()
  }, [addForm, addForm.watch])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const form = { ...values }

      form.maxSpend = +form.maxSpend
      form.minSpend = +form.minSpend

      if (tierId) {
        await updateTierFx({ ...form, id: tierId })
      } else {
        await createTierFx(form)
      }
      onClose()
    },
    [onClose, tierId],
  )

  const title = tier ? 'Edit Fan Spend List' : 'Create Fan Spend List'

  const buttonText = tier ? 'Save' : 'Create List'

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId={testId}
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">{title}</Dialog.Title>
      </Dialog.Header>
      <FormProvider {...addForm}>
        <form onSubmit={addForm.handleSubmit(handleSubmit)}>
          <FormField
            name="name"
            label="Name"
            showErrors={true}
            className="mt-6"
            placeholder="Enter Name"
            testId="name-input"
          />
          <FormField
            name="minSpend"
            label="Minimum Spend"
            showErrors={true}
            className="mt-6"
            type="number"
            prefix={<span className="text-secondary-300">$</span>}
            testId="min-spend-input"
          />
          <FormField
            name="maxSpend"
            label="Maximum Spend"
            showErrors={true}
            className="mt-6"
            type="number"
            prefix={<span className="text-secondary-300">$</span>}
            testId="max-spend-input"
          />
          <FormCheckbox
            name="includeExpiredFans"
            className="mt-6"
            label="Include expired Fans"
            testId="include-expired-fans-checkbox"
          />
          {error ? (
            <Error
              error={error}
              className="mt-4"
              testId={tier ? 'editing-error' : 'adding-error'}
            />
          ) : null}
          <Dialog.ClosableFooter onClose={onClose}>
            <Button
              type="submit"
              isLoading={isUpdating || isCreating}
              testId="submit-button"
            >
              {buttonText}
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
